import { useEffect } from 'react';
import { Link } from 'react-router';
import { AppMark, APP_NAME } from '../components/shell/AppMark.js';
import { Segmented } from '../components/ui/Segmented.js';
import { useTheme } from '../lib/theme-context.js';
import type { Theme } from '../lib/theme-context.js';

const THEME_OPTIONS = [
  { value: 'system', label: 'System', icon: 'bx-desktop' },
  { value: 'light', label: 'Light', icon: 'bx-sun' },
  { value: 'dark', label: 'Dark', icon: 'bx-moon' },
] as const satisfies readonly { value: Theme; label: string; icon: string }[];

/**
 * Spec 247 — Kridt's Sign-in sheet (`.hub-signin`): the first screen when
 * signed out, identical in every hub app. The large mark, the name at
 * display-2, one line, the one action at full width, a quiet "How it works",
 * and the theme control.
 *
 * Before this a signed-out visitor went straight from any 401 to Auth0's
 * Universal Login — which the 2026-09-27 review found UNBRANDED: a black page,
 * Auth0's logo, "Log in | orbit-geo-observatory". Branding that page is Orbit's
 * job; making sure a visitor meets this app first is ours.
 *
 * LOAD-BEARING: this route is matched OUTSIDE AuthProvider and BrandProvider
 * (App.tsx), and it makes no protected request. Both providers call
 * /api/v1/me or /api/v1/brands on mount; inside them, a signed-out visitor
 * would 401, be sent here, reload, 401 again — an infinite loop that never
 * reproduces locally, because ORBIT_DEV bypasses auth. Gate 247 forces real
 * 401s by request interception and counts navigations.
 */
export function SignInPage() {
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    document.title = `Sign in — ${APP_NAME}`;
  }, []);

  return (
    <main className="hub-signin" data-testid="signin-page">
      <section className="hub-signin__sheet" aria-labelledby="signin-title">
        <AppMark large />
        <h1 id="signin-title" className="hub-signin__title">{APP_NAME}</h1>
        <p className="hub-signin__lede">
          How AI engines describe your brand, where it goes missing, and what to change.
        </p>
        {/* A plain link, not a router Link: /auth/login is a server route that
            redirects to the identity provider. */}
        <a href="/auth/login" className="hub-btn hub-btn--primary hub-signin__action" data-testid="signin-action">
          Sign in with Orbit
        </a>
        <p className="hub-signin__note">
          <Link to="/signin/how-it-works" data-testid="signin-how-it-works">How it works</Link>
        </p>
        <Segmented options={THEME_OPTIONS} value={theme} onChange={setTheme} label="Theme" testId="signin-theme" />
      </section>
    </main>
  );
}
